import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { userService } from '../services/userService';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-white);
  padding: 20px;
`;

const RegisterCard = styled.div`
  background: var(--color-white);
  border: 2px solid var(--color-light-gray);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Logo = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  font-weight: 900;
  color: var(--color-black);
  text-align: center;
  margin-bottom: 10px;
  letter-spacing: 1px;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: var(--color-gray);
  text-align: center;
  margin-bottom: 30px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-black);
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid var(--color-light-gray);
  font-size: 1rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-blue);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const InputWithButton = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button<{ $disabled?: boolean }>`
  background-color: ${props => props.$disabled ? '#ccc' : 'var(--color-black)'};
  color: var(--color-white);
  border: none;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: var(--color-blue);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 102, 255, 0.3);
  }
`;

const CodeButton = styled(Button)`
  white-space: nowrap;
  min-width: 120px;
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 0.9rem;
  color: var(--color-gray);
`;

const LinkStyled = styled(Link)`
  color: var(--color-blue);
  text-decoration: none;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee;
  color: #c33;
  padding: 12px;
  font-size: 0.9rem;
  text-align: center;
  border-radius: 0;
`;

const SuccessMessage = styled.div`
  background-color: #efe;
  color: #363;
  padding: 12px;
  font-size: 0.9rem;
  text-align: center;
  border-radius: 0;
`;

const InfoText = styled.p`
  font-size: 0.85rem;
  color: var(--color-gray);
  margin-top: -10px;
`;

interface RegisterScreenProps {
  onLogin: () => void;
}

const RegisterScreen: React.FC<RegisterScreenProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 处理注册
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // 前端验证
    if (!email || !username || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      // 使用 Firebase 注册
      await userService.register(email, password, username);
      setSuccess('Registration successful! Please check your email to verify your account. Redirecting...');
      
      // 注册成功后自动登录并跳转
      setTimeout(() => {
        onLogin();
        navigate('/');
      }, 3000);
    } catch (error: any) {
      console.error('Registration error:', error);
      const errorMessage = error.message || 'Registration failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Container>
      <RegisterCard>
        <Logo>ACCORD</Logo>
        <Subtitle>Create your account</Subtitle>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              name="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              minLength={6}
              required
            />
            <InfoText>Password must be at least 6 characters</InfoText>
          </InputGroup>

          <InputGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              required
            />
          </InputGroup>

          <Button type="submit" disabled={isLoading} $disabled={isLoading}>
            {isLoading ? 'Creating account...' : 'Sign Up'}
          </Button>
        </Form>

        <LoginLink>
          Already have an account? <LinkStyled to="/login">Login</LinkStyled>
        </LoginLink>
      </RegisterCard>
    </Container>
  );
};

export default RegisterScreen;
